<template>
    <div class="content">
        <div class="md-layout">
            <div class="md-layout-item">
                <div class="custom-toolbar">
                    <div class="custom-toolbar-start">
                        <filter-container
                            :class="$root.isTablet ? 'tablet-filter-container' : ''"
                            :filters="issueFilter"
                            :filter="filter"
                            @filter="handleFilterOrSearch"
                        />
                    </div>
                    <div class="custom-toolbar-end">
                        <div>
                            <md-button
                                title="Refresh List"
                                class="md-primary md-just-icon md-round pull-right header-button"
                                @click="handleRefresh"
                            >
                                <md-icon>refresh</md-icon>
                            </md-button>
                        </div>
                        <create-issue class="tablet-button-margin" @issueCreated="handleIssueCreated" />
                    </div>
                </div>
            </div>
        </div>
        <div class="md-layout">
            <div
                class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100"
                :class="total < pagination.perPage ? 'margin-space-bot' : ''"
            >
                <md-card>
                    <md-card-header class="md-card-header-icon md-card-header-green">
                        <div class="card-icon">
                            <md-icon>report_problem</md-icon>
                        </div>
                        <h4 class="title">Issues</h4>
                    </md-card-header>
                    <md-card-content class="body-list">
                        <div v-if="!isListLoading">
                            <md-table class="context-menu-support">
                                <md-table-row>
                                    <md-table-head v-if="isMobileView">Status</md-table-head>
                                    <md-table-head>Reference</md-table-head>
                                    <md-table-head>Title</md-table-head>
                                    <md-table-head v-if="!isMobileView">Related To</md-table-head>
                                    <md-table-head v-if="!isMobileView">Status</md-table-head>
                                    <md-table-head v-if="!isMobileView" class="hide-column">Created By</md-table-head>
                                    <md-table-head v-if="!isMobileView" class="hide-column">Created Date</md-table-head>
                                    <md-table-head v-if="!isMobileView">Last Updated By</md-table-head>
                                    <md-table-head v-if="!isMobileView" class="hide-column">Last Updated</md-table-head>
                                </md-table-row>

                                <md-table-row
                                    v-for="item in issuesList"
                                    :key="item.issueId"
                                    @click.stop="showIssueDetailsSidebar(item)"
                                >
                                    <md-table-cell v-if="isMobileView">
                                        <issue-status-button
                                            :issue="item"
                                            :show-mobile-style="true"
                                            @statusUpdated="onChangedIssueStatus"
                                        />
                                    </md-table-cell>
                                    <md-table-cell>
                                        {{ item.issueReference }}
                                    </md-table-cell>
                                    <md-table-cell class="title-container">
                                        {{ item.title }}
                                    </md-table-cell>
                                    <md-table-cell v-if="!isMobileView">
                                        <issue-routed-entity :item="item" />
                                    </md-table-cell>
                                    <md-table-cell v-if="!isMobileView" class="status-container">
                                        <issue-status-button :issue="item" @statusUpdated="onChangedIssueStatus" />
                                    </md-table-cell>
                                    <md-table-cell v-if="!isMobileView" class="hide-column">
                                        {{ item.createdByName }}
                                    </md-table-cell>
                                    <md-table-cell v-if="!isMobileView" class="hide-column">
                                        {{ item.createdDate | dateFormat(DATE_TYPES.standardDate) }}
                                    </md-table-cell>
                                    <md-table-cell v-if="!isMobileView">
                                        {{ item.lastModifiedByName }}
                                    </md-table-cell>
                                    <md-table-cell v-if="!isMobileView" class="hide-column">
                                        {{ item.lastModifiedDate | dateFormat(DATE_TYPES.standardDateTime) }}
                                    </md-table-cell>
                                </md-table-row>
                            </md-table>
                            <div v-if="issuesList.length == 0 && !isLoading">
                                <p class="no-result-message">No results matching your search/filter could be found.</p>
                            </div>
                        </div>
                        <div v-else>
                            <div class="stopbar-loader">
                                <fade-loader :loading="loading" color="#333333" />
                                <span>LOADING</span>
                            </div>
                        </div>
                    </md-card-content>
                </md-card>
            </div>
        </div>
        <md-card-actions class="page-footer" md-alignment="space-between">
            <div>
                <p v-if="total === pagination.perPage" class="card-category">
                    Page {{ pagination.currentPage }} of many
                </p>
                <p v-else class="card-category">Page {{ pagination.currentPage }} of {{ totalPages }}</p>
            </div>
            <pagination
                v-model="pagination.currentPage"
                class="pagination-no-border pagination-success"
                :per-page="pagination.perPage"
                :total="total"
                :enable-first-page-link="true"
                :enable-last-page-link="true"
                @change-page="handleChangePage($event, pagination.perPage)"
            />
        </md-card-actions>
        <transition name="slide">
            <issue-sidebar
                v-if="showIssueDetails"
                :issue-id="issueId"
                :data="issueDetails"
                v-click-outside="toggleIssueDetailsWindow"
                @changeIssueStatus="onChangedIssueStatus"
                @showAddNotes="handleAddIssueNotes"
                @closeModal="toggleIssueDetailsWindow"
            />
        </transition>
    </div>
</template>

<script>
/* eslint-disable import/extensions */
import { mapGetters } from 'vuex';
import moment from 'moment';
import FadeLoader from 'vue-spinner/src/FadeLoader';
import { PAGINATION_DEFAULTS } from '@/utils/defaults';
import { Pagination, FilterContainer } from '@/components';
import { handleRequests, showErrorMessage } from '@/helpers';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { DATE_TYPES, FILTERS, ISSUE_STATUSES, FILTER_IDS } from '@/utils/constants';
import IssueSidebar from './IssueSidebar';
import { CreateIssue, IssueRoutedEntity } from './Components';
import { IssueStatusButton } from './Buttons';
/* eslint-enable import/extensions */

export default {
    name: 'IssueList',
    components: {
        IssueSidebar,
        Pagination,
        CreateIssue,
        FadeLoader,
        IssueStatusButton,
        FilterContainer,
        IssueRoutedEntity
    },
    mixins: [GeneralMixin],
    async created() {
        const issueQueryRef = this.$route.query.ref;
        this.filter.searchText = issueQueryRef;

        this.setupFilter(issueQueryRef);
    },
    async mounted() {
        const currentPage = Number(this.$route.query.currentPage) ?? 1;

        this.pagination.currentPage = currentPage;
        this.pagination.perPage = 25;

        this.assignIssues();
        await this.$store.dispatch('team/FETCH_PENDING_ISSUES');
        window.addEventListener('resize', this.handleResize);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
        document.querySelector('.main-panel').removeEventListener('scroll', this.handleScroll);
    },
    data() {
        return {
            issuesList: [],
            showIssueDetails: false,
            issueDetails: {},
            pagination: PAGINATION_DEFAULTS,
            issueId: 0,
            status: null,
            maxPage: 1,
            loading: true,
            isListLoading: false,
            filter: {
                fromDate: moment()
                    .subtract(30, 'd')
                    .format('YYYY-MM-DD'),
                toDate: moment().format('YYYY-MM-DD'),
                relatedEntityType: null,
                issueStatusId: null,
                searchText: '',
                includeDeletedIssues: false
            },
            topScrollPosition: 0,
            currentSearchTextValue: null,
            DATE_TYPES,
            isLoading: false,
            filterComponents: FILTERS,
            issueFilter: [],
            issueStatuses: ISSUE_STATUSES,
            filterIds: FILTER_IDS
        };
    },
    computed: {
        totalPages() {
            return Math.ceil(this.total / this.pagination.perPage);
        },
        total() {
            return this.pagination.total;
        },
        isMobileView() {
            return window.innerWidth <= 768 || this.$root.isMobileOnly;
        },
        ...mapGetters({
            pendingIssues: 'team/pendingIssues'
        })
    },
    methods: {
        setupFilter(issueReference) {
            this.issueFilter = this.filterComponents
                .flatMap((filter) => {
                    switch (filter.id) {
                        case this.filterIds.DateRange:
                            return [
                                {
                                    ...filter,
                                    id: 'fromDate',
                                    label: 'From date',
                                    defaultValue: moment()
                                        .subtract(30, 'd')
                                        .format('YYYY-MM-DD')
                                },
                                {
                                    ...filter,
                                    id: 'toDate',
                                    label: 'To date',
                                    defaultValue: moment().format('YYYY-MM-DD')
                                }
                            ];
                        case this.filterIds.SelectFilter:
                            return [
                                {
                                    ...filter,
                                    id: 'relatedEntityType',
                                    defaultValue: 'All',
                                    title: 'Related Entity Type',
                                    label: 'Type',
                                    options: [
                                        { id: 'Stop', label: 'Stop' },
                                        { id: 'Shipment', label: 'Shipment' },
                                        { id: 'Trip', label: 'Trip' },
                                        { id: 'CustomerInvoice', label: 'Customer Invoice' },
                                        { id: 'TeamMemberInvoice', label: 'Team Member Invoice' }
                                    ]
                                },
                                {
                                    ...filter,
                                    id: 'issueStatusId',
                                    defaultValue: 'All',
                                    title: 'Issue Status',
                                    options: this.issueStatuses.map((issue) => ({
                                        id: issue.issueStatusId,
                                        label: issue.statusName
                                    }))
                                }
                            ];
                        case this.filterIds.TextFilter:
                            return [
                                {
                                    ...filter,
                                    id: 'searchText',
                                    defaultValue: issueReference,
                                    title: 'Search Text',
                                    placeholder: 'Type to search'
                                }
                            ];
                        case this.filterIds.CheckBox:
                            return [
                                {
                                    ...filter,
                                    id: 'includeDeletedIssues',
                                    defaultValue: false,
                                    label: 'Include Deleted Tasks'
                                }
                            ];
                        default:
                            return [];
                    }
                })
                .filter((filter) => filter !== undefined);
        },
        async assignIssues(pageNumber = 1, totalPerPage = 25) {
            try {
                this.isListLoading = true;
                this.issuesList = [];
                this.isLoading = true;

                const payload = {
                    method: 'get',
                    params: {
                        page: pageNumber,
                        take: totalPerPage,
                        ...this.filter
                    }
                };
                const api = `/api/issue/issues`;

                const response = await handleRequests(api, payload);

                if (response && response.data) {
                    this.issuesList = response.data.responses;
                    this.pagination.total = response.data.totalRecordCount;
                }
            } catch (error) {
                const message = 'Could not fetch issues.';
                showErrorMessage(this, message, error);
            } finally {
                this.isLoading = false;
                this.isListLoading = false;
            }
        },
        handleRefresh() {
            this.handleChangePage(this.pagination.currentPage, this.pagination.perPage);
        },
        showIssueDetailsSidebar(item) {
            this.showIssueDetails = true;
            this.issueId = item.issueId;
            this.issueDetails = item;
        },
        async handleChangePage(currentPage = 1, perPage = 25) {
            this.$_handleLoaderState(true);
            this.showIssueDetails = false;
            this.pagination.currentPage = currentPage;
            this.pagination.perPage = perPage;

            this.assignIssues(currentPage, perPage);

            this.$router.replace({ path: this.$route.path, query: { currentPage } });
            this.$_handleLoaderState(false);
        },
        async handleFilterOrSearch(filter) {
            this.assignIssues();
        },
        toggleIssueDetailsWindow() {
            if (!this.$modal.isModalShown && !this.$messageBox.isMessageBoxShown)
                this.showIssueDetails = !this.showIssueDetails;
        },
        handleAddIssueNotes(issueUpdated) {
            // udpate issue for last modified.
            // add new note to logs

            issueUpdated.forEach((updatedIssue) => {
                const issue = this.issuesList.find((issue) => issue.issueId === updatedIssue.issueId);

                issue.lastModifiedDate = updatedIssue.lastModifiedDate;
                issue.lastModifiedByName = updatedIssue.lastModifiedByName;
                issue.lastModifiedByUserId = updatedIssue.lastModifiedByUserId;
                issue.issueStatusId = updatedIssue.issueStatusId;

                if (updatedIssue.issueLogs) {
                    updatedIssue.issueLogs.forEach((log) => {
                        issue.issueLogs.push(log);
                    });
                }
            });
        },
        onChangedIssueStatus(response) {
            const { issueId, issueStatusId, lastModifiedDate, lastModifiedByUserId, lastModifiedByName } = response;

            const issue = this.issuesList.find((item) => item.issueId === issueId);
            this.$set(issue, 'issueStatusId', issueStatusId);
            this.$set(issue, 'lastModifiedDate', lastModifiedDate);
            this.$set(issue, 'lastModifiedByUserId', lastModifiedByUserId);
            this.$set(issue, 'lastModifiedByName', lastModifiedByName);
        },
        handleIssueCreated(data) {
            if (!this.issuesList || this.issuesList.length < this.pagination.perPage) {
                this.issuesList.unshift(...data);
            } else if (!this.issuesList || this.issuesList.length === this.pagination.perPage) {
                this.issuesList.pop();
                this.issuesList.unshift(...data);
            }
        },
        handleResize() {
            this.$forceUpdate();
        }
    }
};
</script>

<style scoped>
.custom-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.custom-toolbar-start {
    display: flex;
    align-items: center;
}

.custom-toolbar-end {
    display: flex;
    align-items: center;
}

.header-button {
    margin-left: 10px;
}

.description-container {
    max-width: 200px;
}

.custom-ellipsis {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
}

.issue-checkbox {
    margin: 0;
}

.checkbox-head {
    margin-left: 8px;
}

.no-result-message {
    text-align: center;
    padding: 20px;
    color: #666;
}

.page-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 20px;
}

.title-container {
    max-width: 300px !important;
    width: 300px !important;
}

@media screen and (max-width: 768px) {
    .title-container {
        max-width: 200px !important;
        width: 200px !important;
    }

    .md-table {
        width: 100%;
    }

    .md-table-cell {
        padding: 8px;
    }

    .custom-toolbar {
        flex-direction: column;
        align-items: stretch;
    }

    .custom-toolbar-end {
        margin-top: 10px;
        justify-content: space-between;
    }
}

.body-list {
    .stopbar-loader {
        position: absolute;
        top: 40%;
        left: 50%;

        span {
            position: absolute;
            margin-top: 50px;
            width: 110px;
            left: calc((100% - 100px) / 2);
            text-align: center;
            font-weight: 600;
        }
    }
}
</style>
